// Aspiration Service - Single Responsibility: Manage aspirations only
export type Aspiration = {
  id: number
  title: string
  description?: string
  status: 'active' | 'completed' | 'paused' | 'cancelled'
  progress: number
  priority: 'low' | 'medium' | 'high' | 'critical'
  parent_dream_id?: number
  target_date?: string
  category?: string
  is_orphan?: boolean
  created_at?: string
  updated_at?: string
}

export type CreateAspirationRequest = {
  title: string
  description?: string
  priority?: 'low' | 'medium' | 'high' | 'critical'
  parent_dream_id?: number
  target_date?: string
  category?: string
  status?: 'active' | 'completed' | 'paused' | 'cancelled'
  progress?: number
}

export type UpdateAspirationRequest = {
  title?: string
  description?: string
  status?: 'active' | 'completed' | 'paused' | 'cancelled'
  progress?: number
  priority?: 'low' | 'medium' | 'high' | 'critical'
  parent_dream_id?: number
  target_date?: string
  category?: string
}

export class AspirationService {
  private baseUrl = '/api/hierarchy/aspirations'

  async getAspirations(): Promise<Aspiration[]> {
    const response = await fetch(this.baseUrl, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      redirect: 'manual' // Don't follow redirects automatically - we'll handle them
    })
    
    // Handle redirects (302) - usually means authentication required
    if (response.status === 302 || response.status === 301 || response.type === 'opaqueredirect') {
      throw new Error('Authentication required. Please log in to access aspirations.')
    }
    
    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json().catch(() => ({ error: 'Failed to fetch aspirations' }))
        throw new Error(error.error || `Failed to fetch aspirations: ${response.status} ${response.statusText}`)
      } else {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication required. Please log in to access aspirations.')
        }
        throw new Error(`Failed to fetch aspirations: ${response.status} ${response.statusText}. Server returned HTML instead of JSON.`)
      }
    }
    
    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text()
      throw new Error(`Invalid response format: Expected JSON but got ${contentType}. Response: ${text.substring(0, 200)}`)
    }
    
    const data = await response.json()
    // Handle array response
    return Array.isArray(data) ? data : data.data || []
  }

  async getAspiration(id: number): Promise<Aspiration> {
    // Use the generic item endpoint: /api/hierarchy/item/<item_type>/<item_id>
    const response = await fetch(`/api/hierarchy/item/aspiration/${id}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `Failed to fetch aspiration with id ${id}` }))
      throw new Error(error.error || `Failed to fetch aspiration with id ${id}`)
    }
    const result = await response.json()
    // The backend returns { data: { item: {...}, parent_chain: [...], children: [...] } }
    const item = result.data?.item || result.item || result
    // If we need full details, fetch from list and find the item
    if (!item.title) {
      const allAspirations = await this.getAspirations()
      const fullAspiration = allAspirations.find(a => a.id === id)
      if (fullAspiration) return fullAspiration
    }
    // Map the basic item structure to Aspiration
    return {
      id: item.id,
      title: item.title || '',
      description: item.description,
      status: item.status || 'active',
      progress: item.progress || 0,
      priority: item.priority || 'medium',
      parent_dream_id: item.parent_dream_id,
      target_date: item.target_date,
      category: item.category || 'general',
      is_orphan: item.is_orphan,
      created_at: item.created_at,
      updated_at: item.updated_at || item.created_at
    }
  }

  async createAspiration(aspiration: CreateAspirationRequest): Promise<Aspiration> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(aspiration)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create aspiration' }))
      throw new Error(error.error || 'Failed to create aspiration')
    }
    const data = await response.json()
    return data.data || data
  }

  async updateAspiration(id: number, updates: UpdateAspirationRequest): Promise<Aspiration> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update aspiration' }))
      throw new Error(error.error || 'Failed to update aspiration')
    }
    const data = await response.json()
    return data.data || data
  }

  async deleteAspiration(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete aspiration' }))
      throw new Error(error.error || 'Failed to delete aspiration')
    }
  }

  async updateAspirationProgress(id: number, progress: number): Promise<Aspiration> {
    return this.updateAspiration(id, { progress })
  }

  async markAspirationComplete(id: number): Promise<Aspiration> {
    return this.updateAspiration(id, { status: 'completed', progress: 100 })
  }
}
